import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Signal,
  WritableSignal,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs';
import { features } from '@consts/text.const';
import { routerSegments } from '@consts/routes';
import { PreloaderColor } from '@enums/shared.enum';
import { IntelligenceModels } from '@models/intelligence.model';
import { CustomObjectStatesActions } from '@states/custom-objects-states/custom-objects-states.action-types';
import { CustomObjectsActions } from '@states/custom-objects/custom-objects.action-types';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import {
  ConfirmDialogModel,
  ConfirmDialogService,
  CustomObjectEditNameDialogData,
  CustomObjectEditNameDialogResult,
  UiButtonComponent,
  UiCustomObjectEditNameDialogComponent,
  UiNoDataComponent,
} from '@ui';
import { IntelligenceResultsService } from '../../../../../services/intelligence-results.service';
import { IntelligenceToolsService } from '../../../../../services/intelligence-tools.service';
import { UiCustomObjectTrainingDialogService } from '../../../../ui-kit/ui-custom-object-training-dialog/ui-custom-object-training-dialog.service';
import { UiCustomObjectStateCardComponent } from '../../../../ui-kit/ui-custom-object-state-card/ui-custom-object-state-card.component';
import { UiPreloaderComponent } from '../../../../ui-kit/ui-preloader/ui-preloader.component';
import { UiSearchComponent } from '../../../../ui-kit/ui-search/ui-search.component';

/**
 * Cmd+K card grid of every organization custom object, modelled on the
 * Database → Custom objects page and rendering the page's own
 * `ui-custom-object-state-card`.
 *
 * Rename and delete reuse the Database page's dialogs and store actions. The
 * successful mutations are reflected back into this render-only response by
 * `IntelligenceResultsService`, so the card grid stays current without adding a
 * new chat-history entry. Filtering remains client-side: `list-custom-objects`
 * returns the whole organization list in one response.
 */
@Component({
  selector: 'app-custom-objects-result',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    UiButtonComponent,
    UiCustomObjectStateCardComponent,
    UiNoDataComponent,
    UiPreloaderComponent,
    UiSearchComponent,
  ],
  templateUrl: './custom-objects-result.component.html',
  styleUrls: ['./custom-objects-result.component.scss'],
})
export class CustomObjectsResultComponent {
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly toolsService = inject(IntelligenceToolsService);
  private readonly trainingDialogService = inject(UiCustomObjectTrainingDialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly store$ = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly translateService = inject(TranslateService);

  protected readonly preloaderColor: typeof PreloaderColor = PreloaderColor;
  protected readonly noData: typeof features.customObjects.noData = features.customObjects.noData;
  protected readonly searchQuery: WritableSignal<string> = signal<string>('');

  protected readonly customObjectsEnabled: Signal<boolean> = this.store$.selectSignal(
    OrganizationSelectors.selectCustomObjectsEnabled,
  );

  protected readonly response: Signal<IntelligenceModels.CustomObjects.ListResponse | null> =
    computed(() => {
      const result = this.resultsService.current();
      return result?.type === 'custom-objects'
        ? (result.data as IntelligenceModels.CustomObjects.ListResponse)
        : null;
    });

  protected readonly totalCount: Signal<number> = computed(() => {
    const response = this.response();
    if (!response) return 0;
    return response.customObjects.length + response.customObjectStates.length;
  });

  /**
   * Standard objects match on their name only — the same rule the page applies,
   * which is why a state-based object is matched on its state labels and camera
   * id instead (it may carry no name at all).
   */
  private readonly filteredCustomObjects: Signal<
    IntelligenceModels.CustomObjects.CustomObject[]
  > = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const customObjects = this.response()?.customObjects ?? [];
    return query
      ? customObjects.filter((item) => item.name?.toLowerCase().includes(query))
      : customObjects;
  });

  private readonly filteredCustomObjectStates: Signal<
    IntelligenceModels.CustomObjects.CustomObjectState[]
  > = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const customObjectStates = this.response()?.customObjectStates ?? [];
    return query
      ? customObjectStates.filter(
          (item) =>
            item.states?.some((state) => state.toLowerCase().includes(query)) ||
            item.cameraId?.toLowerCase().includes(query),
        )
      : customObjectStates;
  });

  /**
   * One flat list for the grid. The keys are type-prefixed because a standard
   * object and a state-based object can share a numeric id.
   */
  protected readonly listItems: Signal<IntelligenceModels.CustomObjects.ListItem[]> = computed(
    () => [
      ...this.filteredCustomObjects().map((item) => ({
        key: `object-${item.customObjectId}`,
        item,
      })),
      ...this.filteredCustomObjectStates().map((item) => ({
        key: `state-${item.id}`,
        item,
      })),
    ],
  );

  public constructor() {
    this.resultsService.setHeaderActions([
      {
        icon: 'ui-icon-new-tab',
        tooltip: 'CUSTOM_OBJECTS_RESULT.OPEN_PAGE',
        onClick: () => this.openCustomObjectsPage(),
      },
    ]);
    this.destroyRef.onDestroy(() => this.resultsService.setHeaderActions([]));

    // `create-custom-object` asks for the wizard here rather than rendering a
    // result of its own — the wizard is a dialog, not a panel view. This is an
    // effect and not a one-off constructor read because the panel reuses this
    // component instance across results of the same type, so a second create
    // call has to reach an already-mounted component.
    effect(() => {
      if (!this.resultsService.createCustomObjectRequest()) return;
      const request = this.resultsService.consumeCreateCustomObjectRequest();
      // The gate withholds exactly the endpoints the wizard needs, so a disabled
      // organization gets the feature-disabled panel and no dialog.
      if (!request || !this.customObjectsEnabled()) return;
      this.createCustomObject(request.prompt);
    });
  }

  protected onSearchQuery(query: string): void {
    this.searchQuery.set(query);
  }

  protected trackByKey(_index: number, listItem: IntelligenceModels.CustomObjects.ListItem): string {
    return listItem.key;
  }

  /**
   * Open the Database page's own training dialog. The panel refreshes itself on
   * save instead of dispatching `getSavedCustomObjectStates()` like the page:
   * this response is a render-only snapshot, not the store list.
   *
   * `prompt` seeds the dialog's description field with what the user told the AI
   * to detect; the template's own buttons open the dialog blank.
   */
  protected createCustomObject(prompt?: string): void {
    this.trainingDialogService
      .openTrainingDialog(prompt)
      .pipe(
        take(1),
        filter((result) => result?.saved === true),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => void this.refreshCustomObjects());
  }

  /**
   * Re-fetch rather than splice the created object in: the object-states flow
   * closes with a name only, so there is no card to append.
   */
  private async refreshCustomObjects(): Promise<void> {
    const response = await this.toolsService.fetchCustomObjects();
    // The panel may have moved on to another result while the dialog was open.
    if (this.resultsService.current()?.type !== 'custom-objects') return;
    this.resultsService.replaceCurrentWithCustomObjects(response);
  }

  protected editName(
    customObject:
      | IntelligenceModels.CustomObjects.CustomObject
      | IntelligenceModels.CustomObjects.CustomObjectState,
  ): void {
    const customObjectId = this.isStateCustomObject(customObject)
      ? customObject.id
      : customObject.customObjectId;
    if (customObjectId === undefined) return;

    this.dialog
      .open<
        UiCustomObjectEditNameDialogComponent,
        CustomObjectEditNameDialogData,
        CustomObjectEditNameDialogResult
      >(UiCustomObjectEditNameDialogComponent, {
        data: {
          currentName: customObject.name ?? '',
          customObjectId,
        },
        width: '500px',
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((result) => result?.saved === true && !!result.newName),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        const newName = result?.newName;
        if (!newName) return;

        if (this.isStateCustomObject(customObject)) {
          this.store$.dispatch(
            CustomObjectStatesActions.updateCustomObjectStateName({
              stateId: customObject.id,
              name: newName,
            }),
          );
          return;
        }

        this.store$.dispatch(
          CustomObjectsActions.updateCustomObjectName({
            customObjectId,
            name: newName,
          }),
        );
      });
  }

  protected remove(
    customObject:
      | IntelligenceModels.CustomObjects.CustomObject
      | IntelligenceModels.CustomObjects.CustomObjectState,
  ): void {
    const displayName = this.isStateCustomObject(customObject)
      ? customObject.name ||
        customObject.states?.join('/') ||
        this.translateService.instant('CUSTOM_OBJECTS_RESULT.DEFAULT_NAME')
      : customObject.name;

    this.confirmDialog
      .open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: this.translateService.instant('CUSTOM_OBJECTS_RESULT.DELETE.TITLE'),
        msg: this.translateService.instant('CUSTOM_OBJECTS_RESULT.DELETE.MESSAGE', {
          name: `"${displayName}"`,
        }),
        confirm: this.translateService.instant('COMMON.DELETE'),
        cancel: this.translateService.instant('COMMON.CANCEL'),
        disableClose: true,
      })
      .pipe(
        take(1),
        filter(
          (result) =>
            result?.selection === ConfirmDialogModel.ConfirmResult.OK &&
            result.type === ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.isStateCustomObject(customObject)) {
          this.store$.dispatch(
            CustomObjectStatesActions.deleteCustomObjectState({ stateId: customObject.id }),
          );
          return;
        }

        if (customObject.customObjectId === undefined) return;
        this.store$.dispatch(
          CustomObjectsActions.deleteCustomObject({
            customObjectId: customObject.customObjectId,
          }),
        );
      });
  }

  private isStateCustomObject(
    customObject:
      | IntelligenceModels.CustomObjects.CustomObject
      | IntelligenceModels.CustomObjects.CustomObjectState,
  ): customObject is IntelligenceModels.CustomObjects.CustomObjectState {
    return 'states' in customObject;
  }

  private openCustomObjectsPage(): void {
    const tree = this.router.createUrlTree([
      '/',
      routerSegments.database,
      routerSegments.customObjects,
    ]);
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin ? `${window.location.origin}${path}` : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
